/**
 * Which device is in use, and what the screen calls each verb on it.
 *
 * Every line that names a key names it in brackets, `[E]`, `[hold E]`, `[R]`,
 * and is written in keyboard words wherever it is written, including inside
 * `sim/`, which knows nothing about devices and must not. The swap happens
 * here, at the last moment, on the way to the screen: the HUD, the fan, the
 * panels and the cards all pass their text through `glyph()`. So the moment a
 * pad is touched every prompt says A and LB, and the moment a key is pressed
 * they say E and F again, and no line anywhere had to learn two spellings.
 */

/** Keyboard name to pad name, for every key the game ever names in brackets. */
export const KEY_TO_PAD = Object.freeze({
  E: 'A',
  F: 'LB',
  G: 'X',
  R: 'RB',
  H: 'View',
  C: 'RT',
  V: 'D-pad up',
  Q: 'D-pad down',
  Space: 'Y',
  Shift: 'L3',
  Esc: 'B',
  'W A S D': 'Left stick',
  Mouse: 'Right stick',
});

let device = 'keyboard';
const listeners = new Set();

export function currentDevice() {
  return device;
}

/** Called by the input layer whenever the last-used device changes. */
export function setDevice(next) {
  if (next !== 'keyboard' && next !== 'gamepad') return;
  if (next === device) return;
  device = next;
  for (const listener of listeners) listener(device);
}

export function onDeviceChange(listener) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

const BRACKETED = /\[(hold |Hold )?(E|F|G|R|H|C|V|Q|Esc|Space|Shift)\]/g;

/** The text with every bracketed key renamed for `as` (the current device by default). */
export function glyph(text, as = device) {
  if (typeof text !== 'string' || as !== 'gamepad') return text;
  return text.replace(BRACKETED, (_, hold, key) => `[${hold ?? ''}${KEY_TO_PAD[key] ?? key}]`);
}

/** One key's name on a device, bare. */
export function keyName(key, as = device) {
  return as === 'gamepad' ? KEY_TO_PAD[key] ?? key : key;
}

/** What each text node said before it was renamed, so it can be renamed back. */
const originals = new WeakMap();

/**
 * Renames every bracketed key in the text under `root`, for the device in use.
 *
 * For panels built once as HTML (the board's Close button, the controls card,
 * a class's Leave button): the HUD renames as it writes, and this catches
 * everything that was written before the device changed. Remembers each text
 * node's original, so switching back gives the keyboard's words exactly.
 */
export function relabel(root, as = device) {
  if (!root?.ownerDocument && !root?.nodeType) return 0;
  const doc = root.ownerDocument ?? root;
  const walker = doc.createTreeWalker(root, 4 /* NodeFilter.SHOW_TEXT */);
  let changed = 0;
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    const had = originals.get(node);
    const source = had !== undefined && glyph(had, 'gamepad') === node.data ? had : had !== undefined && had === node.data ? had : node.data;
    if (!source.includes('[')) continue;
    const next = glyph(source, as);
    originals.set(node, source);
    if (next !== node.data) {
      node.data = next;
      changed += 1;
    }
  }
  return changed;
}
